import * as React from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import AddButton from "./AddButton";
import RemoveButton from "./RemoveButton";
import ArrayFieldCard from "./ArrayFieldCard";
import FormInput from "./FormInput";
import FormTextarea from "./FormTextarea";

const DEFAULT_EXPERIENCE = () => ({
  position: "",
  company: "",
  location: "",
  start_date: "",
  end_date: "",
  description_points: [],
});

/**
 * Reusable experiences input component (position/company/dates + bullet points)
 *
 * @param {Array} experiences - Array of experience objects
 * @param {Function} onChange - Callback: (updatedExperiences) => void
 * @param {Array|String} errors - Validation errors (array per item) OR string for section-level error
 * @param {Function} onFieldFocus - Optional focus handler: (fieldName) => void
 * @param {Function} onFieldBlur - Optional blur handler: (fieldName) => void
 * @param {string} title - Section title
 * @param {string} addLabel - Add button label
 * @param {Function|Object} defaultExperience - Default experience object (or factory) for new entries
 * @param {string} className - Additional CSS classes
 */
function ExperiencesInput({
  experiences = [],
  onChange,
  errors,
  onFieldFocus,
  onFieldBlur,
  title = "Relevant Experience",
  addLabel = "Add Experience",
  showLocation = true,
  positionLabel = "Position",
  companyLabel = "Company",
  locationLabel = "Location",
  startDateLabel = "Start Date",
  endDateLabel = "End Date",
  pointsLabel = "Description Points",
  positionPlaceholder = "e.g. Software Engineer",
  companyPlaceholder = "e.g. Google",
  locationPlaceholder = "e.g. San Francisco, CA",
  startDatePlaceholder = "e.g. Jan 2020",
  endDatePlaceholder = "e.g. Present or Dec 2022",
  pointsPlaceholder = "Describe your responsibilities...",
  positionMaxLength,
  companyMaxLength,
  locationMaxLength,
  startDateMaxLength,
  endDateMaxLength,
  pointsMaxLength = 300,
  defaultExperience = DEFAULT_EXPERIENCE,
  className = "",
}) {
  const safeExperiences = Array.isArray(experiences) ? experiences : [];
  const itemErrors = Array.isArray(errors) ? errors : [];
  const sectionError = typeof errors === "string" ? errors : null;

  const createExperience = React.useCallback(() => {
    if (typeof defaultExperience === "function") return defaultExperience();
    return { ...(defaultExperience || {}) };
  }, [defaultExperience]);

  const emitChange = (next) => {
    if (onChange) onChange(next);
  };

  const addExperience = () => {
    emitChange([...safeExperiences, createExperience()]);
  };

  const removeExperience = (index) => {
    emitChange(safeExperiences.filter((_, i) => i !== index));
  };

  const updateExperienceField = (index, field, value) => {
    const next = [...safeExperiences];
    next[index] = { ...(next[index] || {}), [field]: value };
    emitChange(next);
  };

  const getPoints = (exp) =>
    Array.isArray(exp?.description_points) ? exp.description_points : [];

  const addPoint = (expIndex) => {
    const exp = safeExperiences[expIndex] || {};
    const nextPoints = [...getPoints(exp), ""];
    updateExperienceField(expIndex, "description_points", nextPoints);
  };

  const updatePoint = (expIndex, pointIndex, value) => {
    const exp = safeExperiences[expIndex] || {};
    const nextPoints = getPoints(exp).map((p, idx) => (idx === pointIndex ? value : p));
    updateExperienceField(expIndex, "description_points", nextPoints);
  };

  const removePoint = (expIndex, pointIndex) => {
    const exp = safeExperiences[expIndex] || {};
    const nextPoints = getPoints(exp).filter((_, idx) => idx !== pointIndex);
    updateExperienceField(expIndex, "description_points", nextPoints);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between gap-4">
        <h3 className="section-title text-lg font-semibold">{title}</h3>
        <AddButton onClick={addExperience} variant="secondary">
          {addLabel}
        </AddButton>
      </div>

      {safeExperiences.map((exp, index) => {
        const expErr = itemErrors?.[index] || {};
        const points = getPoints(exp);
        const pointsErr = Array.isArray(expErr?.description_points) ? expErr.description_points : [];

        return (
          <ArrayFieldCard
            key={index}
            title={`Experience ${index + 1}`}
            index={index}
            onRemove={() => removeExperience(index)}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormInput
                label={positionLabel}
                name={`relevant_experience.${index}.position`}
                value={exp?.position || ""}
                onChange={(v) => updateExperienceField(index, "position", v)}
                onFocus={onFieldFocus}
                onBlur={onFieldBlur}
                placeholder={positionPlaceholder}
                maxLength={positionMaxLength}
                error={expErr?.position}
              />

              <FormInput
                label={companyLabel}
                name={`relevant_experience.${index}.company`}
                value={exp?.company || ""}
                onChange={(v) => updateExperienceField(index, "company", v)}
                onFocus={onFieldFocus}
                onBlur={onFieldBlur}
                placeholder={companyPlaceholder}
                maxLength={companyMaxLength}
                error={expErr?.company}
              />

              {showLocation && (
                <FormInput
                  label={locationLabel}
                  name={`relevant_experience.${index}.location`}
                  value={exp?.location || ""}
                  onChange={(v) => updateExperienceField(index, "location", v)}
                  onFocus={onFieldFocus}
                  onBlur={onFieldBlur}
                  placeholder={locationPlaceholder}
                  maxLength={locationMaxLength}
                  error={expErr?.location}
                />
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormInput
                  label={startDateLabel}
                  name={`relevant_experience.${index}.start_date`}
                  value={exp?.start_date || ""}
                  onChange={(v) => updateExperienceField(index, "start_date", v)}
                  onFocus={onFieldFocus}
                  onBlur={onFieldBlur}
                  placeholder={startDatePlaceholder}
                  maxLength={startDateMaxLength}
                  error={expErr?.start_date}
                />

                <FormInput
                  label={endDateLabel}
                  name={`relevant_experience.${index}.end_date`}
                  value={exp?.end_date || ""}
                  onChange={(v) => updateExperienceField(index, "end_date", v)}
                  onFocus={onFieldFocus}
                  onBlur={onFieldBlur}
                  placeholder={endDatePlaceholder}
                  maxLength={endDateMaxLength}
                  error={expErr?.end_date}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label className={cn(expErr?.description_points && "text-destructive")}>
                  {pointsLabel}
                </Label>
                <AddButton onClick={() => addPoint(index)} variant="secondary" size="sm">
                  Add Point
                </AddButton>
              </div>

              {points.length > 0 && (
                <div className="space-y-3">
                  {points.map((point, pointIndex) => (
                    <div key={pointIndex} className="flex gap-2">
                      <div className="flex-1">
                        <FormTextarea
                          name={`relevant_experience.${index}.description_points.${pointIndex}`}
                          value={point || ""}
                          onChange={(v) => updatePoint(index, pointIndex, v)}
                          onFocus={onFieldFocus}
                          onBlur={onFieldBlur}
                          placeholder={pointsPlaceholder}
                          rows={3}
                          maxLength={pointsMaxLength}
                          error={pointsErr?.[pointIndex]}
                        />
                      </div>
                      <RemoveButton
                        onClick={() => removePoint(index, pointIndex)}
                        ariaLabel={`Remove experience ${index + 1} point ${pointIndex + 1}`}
                        variant="ghost"
                        className="hover:bg-destructive/10 hover:text-destructive"
                      />
                    </div>
                  ))}
                </div>
              )}

            </div>
          </ArrayFieldCard>
        );
      })}

      {sectionError && <p className="text-sm font-medium text-destructive">{sectionError}</p>}
    </div>
  );
}

export default ExperiencesInput;
